/*
* Copyright Gregory Coburn 2020-2026, All Rights Reserved, See license for further details
*/

import { AbstractObject, TenantId, uuid } from "@src/app/model/abstract-object";
import { RecurringJob } from "@src/app/model/recurring-job";
import { ServiceRequest } from "@src/app/model/serviceRequest";
import { OmcRole } from '@src/app/model/OmcRole';
import { User } from "@src/app/model/user";
import { AppComponent } from "@src/app/app.component";

export class Omc extends AbstractObject {
    constructor(o: Partial<Omc> = {}) {
        super(o);
    }
    managerId: uuid;
    manager: User;
    adminId: uuid;
    admin: User;
    financeId: uuid;
    finance: User;
    caretakerId: uuid;
    caretaker: User;

    /** Only on the agent/omcList payload - the OMC's nominated request owner, if it has set one. */
    requestOwnerId: uuid;
    requestOwnerName: string;

    /**
     * The current agent looking after this OMC, could be different!
     */
    primaryAgentId: TenantId;

    omcTeamId: TenantId;
    requests: ServiceRequest[] = [];
    recurringJobs: RecurringJob[] = [];

    legalName: string;
    typeId: uuid;
    type: AbstractObject;
    address: string;
    expectedUnits = 0;
    slug: string;
    yearEndMonth: number = AppComponent.isLocal ? 12 : null;
    yearEndDay: number = AppComponent.isLocal ? 31 : null;

    // Only appears on omc list, comes from agency...
    residentSignup: boolean;
    ownerSignup: boolean;
    otherSignup: boolean;
    // Calculated on server
    balance: string; // arrives as string, is bank balance
    budgetTotal: string;// arrives as string
    lastRecDate: string;
    
    gocLastPull: number;
    cycleStatusId: uuid;
    units_count: number;
    open_requests_count: number;
    
    manageBank = true;
    isCurrent: boolean;

    /** The requestOwnerId setting when no user is nominated - see config/services.php */
    static agentRequestOwner = 'agent';

    /**
     * Who a new request goes to when nobody has been named on it. Replicated Server Side in
     * Omc.php Model Class (getDefaultRequestOwner), and applied there by RequestService whenever a
     * request is posted without an owner - so what a form defaults to has to agree with it.
     */
    static getDefaultRequestOwner(omc: Omc): AbstractObject {
        if (omc?.requestOwnerId && omc.requestOwnerId !== Omc.agentRequestOwner) {
            return { id: omc.requestOwnerId, name: omc.requestOwnerName };
        }
        if (omc?.adminId) {
            return { id: omc.adminId, name: omc.admin?.name };
        }
        return { id: omc?.managerId, name: omc?.manager?.name };
    }

    /** Replicated Server Side in Omc.php Model Class  */
    static getUserId(omc: Omc, type: string) {
        return Omc.getRoleUser(omc, type)?.id;
    }

    /**
     * The officer holding an OMC role, as the id and name an owner picker needs - a template names
     * a role, and the field that shows the result renders from the name rather than the id.
     *
     * Reads the officer relations agent/omcList and agent/omcFor eager load
     * (OmcController::withRequestOwner), the same ones getDefaultRequestOwner reads. Every role but
     * manager falls back to the manager, matching the server.
     */
    static getRoleUser(omc: Omc, type: string): AbstractObject {
        if (!omc) {
            return null;
        }
        if (type === OmcRole.admin.id && omc.adminId) {
            return { id: omc.adminId, name: omc.admin?.name };
        }
        if (type === OmcRole.finance.id && omc.financeId) {
            return { id: omc.financeId, name: omc.finance?.name };
        }
        if (type === OmcRole.caretaker.id && omc.caretakerId) {
            return { id: omc.caretakerId, name: omc.caretaker?.name };
        }
        if (!OmcRole.values.find(role => role.id === type)) {
            return null;
        }
        return { id: omc.managerId, name: omc.manager?.name };
    }

    /*static userTypes: AbstractObject[] = [
        { id: 'manager', name: 'Property Manager' },
        { id: 'admin', name: 'Administrator' },
        { id: 'finance', name: 'Finance' },
        { id: 'caretaker', name: 'Caretaker' }
    ]*/
    static omcTypes: AbstractObject[] = [
        { id: 5, name: 'Residential' },
        { id: 10, name: 'Commercial & Residential' },
        { id: 15, name: 'Commercial' },
    ];
}
